import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import Dialog from '@material-ui/core/Dialog';
import Fade from '@material-ui/core/Fade';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import Chip from '@material-ui/core/Chip';
import { setAddMealDialog, addCartItemSeparately } from '../../actions/dineIn';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import AddIcon from '@material-ui/icons/Add';
import RemoveIcon from '@material-ui/icons/Remove';
import lng from '../../misc/lng';
import envConst from '../../misc/envConst';
import { useFontSize } from '../../contexts/FontSizeContext';

const { chiLng } = lng;

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Fade ref={ref} {...props} />;
});

const useStyles = makeStyles((theme) => ({
    root: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden',
    },
    imageHeader: {
        height: '40vh',
        minHeight: '250px',
        maxHeight: '400px',
        position: 'relative',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundColor: '#f5f5f5',
    },
    backButton: {
        position: 'fixed',
        top: theme.spacing(2),
        left: theme.spacing(2),
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        color: 'white',
        zIndex: 1000,
        width: 44,
        height: 44,
        borderRadius: '50%',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
        '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
        },
        // Add safe area for mobile devices
        '@media (max-width: 768px)': {
            top: 'max(env(safe-area-inset-top), 16px)',
            left: 'max(env(safe-area-inset-left), 16px)',
            width: 40,
            height: 40,
        },
    },
    scrollableWrapper: {
        flex: 1,
        overflow: 'auto',
        paddingBottom: theme.spacing(10), // Space for bottom section
        // Handle mobile viewport issues
        minHeight: 0, // Allow flex child to shrink
        WebkitOverflowScrolling: 'touch', // Smooth scrolling on iOS
    },
    contentContainer: {
        display: 'flex',
        flexDirection: 'column',
        minHeight: '60vh', // Use viewport height instead of calc
        flex: 1,
    },
    card: {
        backgroundColor: '#FFFFFF',
        elevation: 2,
        marginTop: -80,
        borderRadius: 12,
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        zIndex: 5,
    },
    scrollableContent: {
        flex: 1,
    },
    cardContent: {
        padding: theme.spacing(2),
    },
    productTitle: {
        fontSize: '20px',
        fontWeight: 'bold',
        textAlign: 'center',
        color: '#212121',
        marginBottom: theme.spacing(1),
    },
    productSubtitle: {
        fontSize: '14px',
        color: '#757575',
        textAlign: 'center',
        marginBottom: theme.spacing(2),
    },
    priceContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        marginBottom: theme.spacing(3),
    },
    currentPrice: {
        fontSize: '18px',
        fontWeight: 'bold',
        color: '#212121',
    },
    originalPrice: {
        fontSize: '14px',
        color: '#BDBDBD',
        textDecoration: 'line-through',
        marginLeft: theme.spacing(1),
    },
    section: {
        marginBottom: theme.spacing(2),
    },
    sectionTitle: {
        fontSize: '16px',
        fontWeight: 'bold',
        color: '#212121',
        marginBottom: theme.spacing(1),
    },
    optionsContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(1),
    },
    optionChip: {
        backgroundColor: '#f5f5f5 !important',
        color: '#212121 !important',
        border: '1px solid #E0E0E0',
        minWidth: 'auto',
        height: '32px',
        '&:hover': {
            backgroundColor: '#e0e0e0 !important',
        },
        '& .MuiChip-label': {
            color: '#212121 !important',
            fontSize: 'inherit !important',
        },
    },
    optionChipSelected: {
        backgroundColor: '#4CAF50 !important',
        color: '#FFFFFF !important',
        border: '1px solid #4CAF50',
        minWidth: 'auto',
        height: '32px',
        '&:hover': {
            backgroundColor: '#45a049 !important',
            border: '1px solid #45a049',
        },
        '& .MuiChip-label': {
            color: '#FFFFFF !important',
            fontSize: 'inherit !important',
        },
    },
    customRequestField: {
        '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            '& fieldset': {
                borderColor: '#E0E0E0',
            },
        },
    },
    bottomSection: {
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: '#FFFFFF',
        padding: theme.spacing(2),
        borderTop: '1px solid #E0E0E0',
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2),
        zIndex: 10,
        // Handle mobile safe areas
        paddingBottom: 'max(env(safe-area-inset-bottom), 16px)',
        paddingLeft: 'max(env(safe-area-inset-left), 16px)',
        paddingRight: 'max(env(safe-area-inset-right), 16px)',
    },
    quantitySection: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2),
    },
    quantityButton: {
        backgroundColor: '#f5f5f5',
        border: '1px solid #E0E0E0',
        borderRadius: '50%',
        width: 40,
        height: 40,
        '&:hover': {
            backgroundColor: '#e0e0e0',
        },
        '&:disabled': {
            backgroundColor: '#f5f5f5',
            color: '#bdbdbd',
        },
    },
    quantityText: {
        fontSize: '18px',
        fontWeight: 'bold',
        color: '#212121',
        minWidth: 30,
        textAlign: 'center',
    },
    actionButton: {
        flex: 1,
        backgroundColor: '#4CAF50',
        color: '#FFFFFF',
        fontSize: '16px',
        fontWeight: 'bold',
        borderRadius: 12,
        padding: theme.spacing(1.5),
        textTransform: 'none',
        '&:hover': {
            backgroundColor: '#45a049',
        },
    },
}));

export default () => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const { getFontSize } = useFontSize();
    const {
        addMealDialog: {
            open, data
        }
    } = useSelector((store) => store.dineInReducer);

    // Dynamic font sizes
    const titleFontSize = getFontSize('mealTitle');
    const subtitleFontSize = getFontSize('subtitle');
    const priceFontSize = getFontSize('priceMain');
    const buttonFontSize = getFontSize('buttonText');
    const chipFontSize = getFontSize('buttonText');



    const {
        menu: { simpleOptionList }
    } = useSelector((store) => store.menuReducer);

    const [qty, setQty] = useState(1);
    const [simpleOptList, setSimpleOptList] = useState([]);
    const [customRequest, setCustomRequest] = useState('');
    const [img, setImg] = useState(envConst.DEFAULT_IMG_PATH);

    const handleClickSimpleOpt = (optionName) => {
        setSimpleOptList(
            simpleOptList.map(
                (sOpt) => {
                    if (optionName === sOpt.optionName) {
                        return {
                            ...sOpt,
                            selected: !sOpt.selected,
                        };
                    }
                    return sOpt;
                }
            )
        );
    };

    const handleQtyAdd = () => {
        if (qty < 99) {
            setQty(qty + 1);
        }
    };

    const handleQtyReduce = () => {
        if (qty > 1) {
            setQty(qty - 1);
        }
    };

    const handleClose = () => {
        dispatch(setAddMealDialog(false, {}));
    };

    const handleConfirm = () => {
        const mealToAdd = {
            ...data,
            qty,
            price: data.price,
            simpleOptions: simpleOptList.filter(
                so => so.selected
            ).map(
                so => so.optionName
            ),
            customRequest: customRequest.trim(),
        };
        dispatch(addCartItemSeparately(mealToAdd));
        handleClose();
    };

    const onEnter = () => {
        if (data && data.img) {
            setImg(`${envConst.IMAGE_PATH}${data.img}`);
        } else {
            setImg(envConst.DEFAULT_IMG_PATH);
        }

        setSimpleOptList(
            simpleOptionList.map(
                (opt) => ({
                    ...opt,
                    selected: false,
                })
            )
        );
        setQty(1);
        setCustomRequest('');
    };

    const handleImgError = () => {
        setImg(envConst.DEFAULT_IMG_PATH);
    };

    const getTotalPrice = () => {
        return (qty * (data?.price || 0)).toFixed(2);
    };

    return (
        <Dialog
            open={open}
            TransitionComponent={Transition}
            onClose={handleClose}
            fullScreen
            TransitionProps={{ onEnter: onEnter }}
        >
            <div className={classes.root}>
            {/* Scrollable Wrapper for Image Header and Content */}
            <div className={classes.scrollableWrapper}>
                {/* Image Header */}
                <div 
                    className={classes.imageHeader}
                    style={{
                        backgroundImage: `url(${img})`,
                    }}
                >
                    <IconButton
                        className={classes.backButton}
                        onClick={handleClose}
                        aria-label="back"
                    >
                        <ArrowBackIcon />
                    </IconButton>
                </div>

                {/* Content Container */}
                <div className={classes.contentContainer}>
                    <Card className={classes.card}>
                        <div className={classes.scrollableContent}>
                            <CardContent className={classes.cardContent}>
                                {/* Product Title and Subtitle */}
                                <Typography
                                    variant="inherit"
                                    className={classes.productTitle}
                                    style={{ fontSize: titleFontSize }}
                                >
                                    {data?.name || ''}
                                </Typography>

                                {data?.description && (
                                    <Typography
                                        variant="inherit"
                                        className={classes.productSubtitle}
                                        style={{ fontSize: subtitleFontSize }}
                                    >
                                        {data.description}
                                    </Typography>
                                )}

                                {/* Price */}
                                <div className={classes.priceContainer}>
                                    <Typography
                                        variant="inherit"
                                        className={classes.currentPrice}
                                        style={{ fontSize: priceFontSize }}
                                    >
                                        ${data?.price || '0.00'}
                                    </Typography>
                                    {/* Add original price if there's a discount */}
                                </div>

                                {/* Options Section */}
                                {simpleOptList.length > 0 && (
                                    <div className={classes.section}>
                                        <Typography
                                            variant="inherit"
                                            className={classes.sectionTitle}
                                            style={{ fontSize: getFontSize('bodyText') }}
                                        >
                                            {chiLng.simpleOptTitle}
                                        </Typography>
                                        <div className={classes.optionsContainer}>
                                            {simpleOptList.map((opt, idx) => (
                                                <Chip
                                                    key={idx}
                                                    label={opt.optionName}
                                                    onClick={() => handleClickSimpleOpt(opt.optionName)}
                                                    className={opt.selected ? classes.optionChipSelected : classes.optionChip}
                                                    clickable
                                                    variant="outlined"
                                                    size="medium"
                                                    style={{ fontSize: chipFontSize }}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Custom Request Section */}
                                <div className={classes.section}>
                                    <TextField
                                        fullWidth
                                        variant="outlined"
                                        placeholder={chiLng.customRequestPlaceholder}
                                        value={customRequest}
                                        onChange={(e) => setCustomRequest(e.target.value)}
                                        className={classes.customRequestField}
                                        multiline
                                        minRows={2}
                                    />
                                </div>
                            </CardContent>
                        </div>
                    </Card>
                </div>
            </div>

            {/* Sticky Bottom Section with Quantity and Action Button */}
            <div className={classes.bottomSection}>
                        {/* Quantity Selector */}
                        <div className={classes.quantitySection}>
                            <IconButton
                                className={classes.quantityButton}
                                onClick={handleQtyReduce}
                                disabled={qty <= 1}
                            >
                                <RemoveIcon />
                            </IconButton>

                            <Typography
                                variant="inherit"
                                className={classes.quantityText}
                                style={{ fontSize: getFontSize('priceMain') }}
                            >
                                {qty}
                            </Typography>

                            <IconButton
                                className={classes.quantityButton}
                                onClick={handleQtyAdd}
                                disabled={qty >= 99}
                            >
                                <AddIcon />
                            </IconButton>
                        </div>

                        {/* Action Button */}
                        <Button
                            className={classes.actionButton}
                            onClick={handleConfirm}
                            style={{ fontSize: buttonFontSize }}
                        >
                            {`${chiLng.addOrder} - $${getTotalPrice()}`}
                        </Button>
                    </div>
                </div>
        </Dialog>
    );
};
